import React, {ChangeEvent, useState} from "react";
import {Formik, ErrorMessage, Field, Form} from "formik";
import {Card, Img, Input} from "@varymadellc/my-weed-club-component-library";
import {Button} from "@varymadellc/my-weed-club-component-library";
import cx from "classnames";
import {useSiteMessage} from "@/providers/SiteMessageProvider";
import {useRouter} from "next/router";
import useSignUpForm from "@/hooks/useSignUpForm";
import Link from "next/link";
import {formValidation} from "@/utils/formValidation";
import Page from "@/layout/Page";

export default function SignUp() {
    const navigate = useRouter();

    const {setMessage} = useSiteMessage();

    const [agrees, setAgrees] = useState(false);

    const {signupSubmit} = useSignUpForm(agrees);

    const agreesHandler = ({target}: ChangeEvent<any>) => {
        const {checked} = target;

        setAgrees(Boolean(checked));
    }

    return (
        <Page title="Sign Up">
            <div className="relative p-2 mb-4 overflow-hidden">
                <Card className="flex-1 bg-secondary flex flex-row gap-10 mb-4">
                    <div className="flex flex-1 flex-col text-black gap-4 p-2">
                        <div className="flex-1 flex justify-center">
                            <div className="relative overflow-hidden max-h-[600px] max-w-[600px] justify-center items-center rounded-lg">
                                <div className="bg-gradient-to-b from-transparent to-black absolute w-full bottom-0 h-[50%]">&nbsp;</div>
                                <img src="/imgs/diary-full.png" alt="lounge" className="w-full"/>
                            </div>
                        </div>
                        <div className="flex-1 text-justify text-lg text-white">
                            <p>
                                Welcome to MyWeed.Club, where our shared passion for cannabis unites us in a unique
                                online community.
                            </p>
                            <p>
                                We&apos;re deeply enthusiastic about every facet of the cannabis experience – from the
                                delightful aroma when it ignites, to the intricate flavors of terpenes, and that
                                unmistakable sensation as THC takes hold.
                            </p>
                            <p>
                                As a member, you&apos;ll enjoy a range of benefits, including access to your
                                personalized
                                profile, captivating galleries, site-wide chat, and direct messaging.
                            </p>
                            <p>
                                One exceptional feature that has us excited is
                                the public weed diary. It&apos;s a platform designed for you to share your cannabis
                                journey&apos;s
                                experiences, insights, and reflections. Plus, rest assured, your entries are private by
                                default.
                            </p>
                            <p>
                                Explore the community&apos;s collective wisdom here: <Link href="/diary/public">Public
                                Diary Entries</Link>
                            </p>
                            <p>
                                This shared knowledge is an invaluable resource for everyone. You&apos;ll discover brand
                                recommendations, strains worth savoring, and ones to steer clear of, all based on
                                genuine experiences.
                            </p>
                            <p>
                                Join us in this welcoming space, reminiscent of your favorite lounge – a safe place for
                                all things cannabis. Signing up is a breeze, and the atmosphere is refreshingly
                                pressure-free. We can&apos;t wait to welcome you aboard!
                            </p>
                        </div>
                    </div>
                </Card>
                <div className="-mx-4 relative p-2 mb-4">
                    <div
                        className={`flex flex-row flex-wrap w-full gap-4 bg-dark-black lg:rounded-lg bg-center bg-cover bg-[url('/imgs/lounge.png')] border border-primary`}>
                        <div className="flex flex-row flex-wrap p-2 gap-2">
                            <Card inline={true} className="w-[50%] flex-1">
                                <h2>
                                    Members Chat
                                </h2>
                                <br/>
                                <p>
                                    A members only chat to talk with other cannabis users such as yourself.
                                    Send a chat today, get to know others who enjoy what you do!
                                </p>
                            </Card>
                            <Card inline={true} className="w-[50%] flex-1">
                                <h2>
                                    Diary
                                </h2>
                                <br/>
                                <p>
                                    Write down your favorite strains for next time. Journal what your experience is
                                    like.
                                    Read others entries and learn.
                                </p>
                            </Card>
                            <Card inline={true} className="w-[50%] flex-1">
                                <h2>
                                    Galleries
                                </h2>
                                <br/>
                                <p>
                                    Come show off your gear, supplies and other stoner activities with people who enjoy
                                    it
                                    as much as you do.
                                </p>
                            </Card>
                            <Card inline={true} className="w-[50%] flex-1">
                                <h2>
                                    Lounge
                                </h2>
                                <br/>
                                <p>
                                    A virtual lounge to hangout in. Come chill with us and talk about your favorite
                                    strains, brands and more.
                                </p>
                            </Card>
                        </div>
                        <div className="m-10 w-full flex flex-col justify-center">
                            <Card className="!p-10">
                                <div className="flex flex-col">
                                    <Formik
                                        initialValues={{
                                            username: '',
                                            fullName: '',
                                            dob: '',
                                            email: '',
                                            password: '',
                                            confirmPassword: '',
                                            zipCode: ''
                                        }}
                                        validate={formValidation}
                                        onSubmit={async (values) => {
                                            try {
                                                const response = await signupSubmit(values)

                                                if (response) {
                                                    setMessage('You have successfully signed up!');

                                                    navigate.push('/?scene=welcome#signinForm');

                                                    return;
                                                }
                                            } catch (error: any) {
                                                setMessage(error.message);
                                            }
                                        }}
                                    >
                                        {({isSubmitting}) => (
                                            <Form id="signupForm">
                                                <h2 className="text-primary text-3xl">Sign Up For Free!</h2>
                                                <br/>
                                                {[
                                                    {key: 'username', title: 'Username'},
                                                    {key: 'email', title: 'Email'},
                                                    {key: 'fullName', title: 'Full Name'},
                                                    {key: 'dateOfBirth', title: 'Date of Birth', type: 'date'},
                                                    {key: 'zipCode', title: 'Zip Code'},
                                                    {key: 'password', title: 'Password', type: 'password'},
                                                    {
                                                        key: 'confirmPassword',
                                                        title: 'Confirm Password',
                                                        type: 'password'
                                                    },
                                                ].map<any>((item: { key: string; title: string, type?: string }) => {
                                                    return (
                                                        <div key={item.title} className="mb-2">
                                                            <label htmlFor={item.key}
                                                                   className="form-label">{item.title}</label>
                                                            <br/>
                                                            <Field
                                                                className={cx("bg-gray-700 border border-black rounded-sm p-1 text-white", {'w-full': item.type !== 'date'})}
                                                                type={item.type || 'text'}
                                                                name={item.key}/>
                                                            <div className="h-[1.2rem]">
                                                                <ErrorMessage className="text-red-500 fs-6 bg-black/50 m-1 rounded"
                                                                              name={item.key}
                                                                              component="div"/>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                <label>
                                                    <Input type="checkbox" onChange={agreesHandler}
                                                           value="true"/> I&apos;m
                                                    21+ years of age or
                                                    18+ with a medical card and
                                                    it&apos;s legal in my state to use cannabis
                                                    and I agree to the <Link target="_blank" className="underline"
                                                                             href="/terms-and-conditions">Terms
                                                    and Conditions</Link>
                                                </label>
                                                <br/>
                                                <br/>
                                                <div id="captcha">&nbsp;</div>
                                                <div className="mx-auto text-center">
                                                    <Button type="submit"
                                                            className="form-control btn btn-secondary"
                                                            disabled={isSubmitting}>Sign Up Free
                                                    </Button>
                                                </div>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>
                <div className="flex-1 mx-auto">
                    <p>
                        Note, We use a single cookie to give us ability to give you cool features, without it the site
                        wouldn&apos;t
                        work.
                    </p>
                    <p>
                        However, we also use google analytics gtag function just to get some insights into what
                        parts of the site are being utilized. We do NOT pass personal information you enter into our
                        site
                        when using
                        analytics.
                    </p>
                    <p>
                        They have their own cookies. We may in the future build in an option
                        to disable the google analytics per users request, but for now we do not have that option.
                        If you sign up, you understand what was mentioned. You may contact us at <a href="mailto: team@myweed.club">team@myweed.club</a>
                         with questions.
                    </p>
                </div>
            </div>
            <br/>
        </Page>
    );
}